// vg-binding: CONTEXT:decisions  (P999.11.D-09/D-14/D-15/D-16 tokens + D-04 dark-ok)
// apps/admin/src/pages/billing/TopupQueuePage.tsx
//
// Phase 3.2 Wave 11 Task 32 — Admin Topup Queue page.
//
// Per CONTEXT.md (admin topup approval UX) + (suspicious flag
// race-loser toast). Covers goals: G-TOPUP-10, G-FRAU.
// Reference design PNG: `.vg/phases/03.2-topup-withdraw-gateway-fx/designs/admin-topup-queue.png`
// (sha256 8416ef944a9038358fb378ec61336a2956faf6458d6e40837613d1870680b1a0).
//
// Endpoints consumed (Task 14, API-CONTRACTS §Topup-Admin):
//   GET    /api/v1/admin/topup-requests?status=&merchant=&gateway=&date_from=&date_to=&flagged=
//   POST   /api/v1/admin/topup-requests/:id/approve   (Idempotency-Key required)
//   POST   /api/v1/admin/topup-requests/:id/reject    (Idempotency-Key required)
//   POST   /api/v1/admin/merchants/:merchantId/topup-requests/:id/flag-suspicious
//   GET    /api/v1/admin/topup-requests/export        (CSV stream; opens new tab)
//
// UI structure (per fingerprint task-32.fingerprint.md):
//   - Header (title + "Oldest first · N in queue" pill + Export CSV button)
//   - Quick filter tabs: All / Pending / Approved / Rejected / Flagged
//   - Filter bar: merchant, gateway, date range
//   - Table (FIFO oldest-first sort)
//   - Side drawer with full detail + actions (slide-in from right)
//   - Approve confirm dialog (idempotency key auto-generated via crypto.randomUUID)
//   - Reject form within drawer (reason required ≥3 chars + optional internal_note)
//   - Race losers (409/410) → toast "Đã được xử lý bởi admin khác" + refresh
//   - URL state sync (status, merchant, gateway, date_from, date_to, flagged) per VG R7.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { JSX } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { apiClient, resolveApiBaseUrl } from '@printway/shared/api';
import {
  Button,
  Card,
  CardContent,
  Input,
  MerchantPicker,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  cn,
  useToast,
  PageHeader,
} from '@printway/ui';
import { ConfirmDialog } from '../../components/ui/ConfirmDialog.js';
import { formatDual } from '@vollxssp/utils';
import { PageListFooter } from '../../components/datatable/PageListFooter.js';
import { useTranslation } from '../../i18n/useTranslation.js';

// Phase 8.2 Wave 2 Task 06 — Finance Topup Reviews tab imports.
// Hoisted to the top of the file so TS keeps every `import` declaration
// before any other top-level code (the new `TopupReviewQueuePage` lives
// below the legacy Phase 3.2 `TopupQueuePage` further down).
import { financeKeys } from '../../api/queries/financeKeys.js';
import {
  approveTopup,
  listTopupReviews,
  rejectTopup,
  type ApproveTopupResponse,
  type RejectTopupResponse,
} from '../../api/finance.api.js';
import { Money } from '../../components/ui/Money.js';
import { BillingStatusBadge } from '../../components/billing/BillingStatusBadge.js';
import {
  TopupProofReviewModal,
  type TopupRejectReason as ModalTopupRejectReason,
} from '../../features/billing/TopupProofReviewModal.js';
import { getUserMessage } from '../../lib/errorHandler.js';
import type {
  TopupReviewListFilters,
  TopupReviewRow,
  TopupReviewSlaBadge,
} from '../../types/finance.js';

// ---------------------------------------------------------------------------
// Types — mirror API-CONTRACTS §Topup-Admin (AdminTopupRequestItem +
// GetTopupRequestResponse). Hand-rolled because shared/api/types.generated.ts
// is still a stub for Phase 3.2 endpoints (codegen lands at /vg:test).
// ---------------------------------------------------------------------------

type TopupStatus = 'pending' | 'approved' | 'rejected' | 'expired';

interface AdminTopupRequestItem {
  id: string;
  status: TopupStatus;
  gateway: string;
  tier: 'tier1' | 'tier2';
  original_amount: number;
  original_currency: string;
  usd_amount: number | null;
  usd_amount_estimated: number | null;
  fx_rate_locked: number | null;
  flagged_suspicious: boolean;
  created_at: string;
  approved_at: string | null;
  /**
   * Backend enriches each row via `$lookup` against `merchant_memberships` →
   * `users` (closes REVIEW-FEEDBACK B-01). Marked optional defensively so
   * historical rows pre-lookup or rows where the owner user is missing
   * (deleted membership) still render without crashing the page.
   */
  merchant?: {
    id: string;
    name: string;
    email: string;
  };
}

interface AdminTopupListResponse {
  data: AdminTopupRequestItem[];
  next_cursor: string | null;
}

interface TopupRequestDetail extends AdminTopupRequestItem {
  merchant_id: string;
  linked_account_id: string | null;
  transaction_id: string | null;
  receipt_file_id: string | null;
  note: string | null;
  approved_by_admin_id: string | null;
  rejected_reason: string | null;
}

interface ErrorEnvelope {
  error?: { code?: string; message?: string };
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const QUICK_TABS: Array<{ key: 'all' | TopupStatus | 'flagged'; labelKey: string }> = [
  { key: 'all', labelKey: 'topup_review.queue.tab.all' },
  { key: 'pending', labelKey: 'topup_review.queue.tab.pending' },
  { key: 'approved', labelKey: 'topup_review.queue.tab.approved' },
  { key: 'rejected', labelKey: 'topup_review.queue.tab.rejected' },
  { key: 'flagged', labelKey: 'topup_review.queue.tab.flagged' },
];

// Radix forbids SelectItem value="" — sentinel for the "all gateways" option.
const ALL_VALUE = '__all__';

const GATEWAY_OPTIONS = [
  'paypal',
  'stripe',
  'payoneer',
  'lianlian',
  'sunrate',
  'pingpong',
  'worldfirst',
  'airwallex',
  'bank',
];

function relativeTime(
  iso: string,
  t: (key: string, vars?: Record<string, unknown>) => string,
): string {
  const then = new Date(iso).getTime();
  const now = Date.now();
  const diffMs = now - then;
  const sec = Math.floor(diffMs / 1000);
  if (sec < 60) return t('topup_review.relative.seconds', { count: sec });
  const min = Math.floor(sec / 60);
  if (min < 60) return t('topup_review.relative.minutes', { count: min });
  const hr = Math.floor(min / 60);
  if (hr < 24) return t('topup_review.relative.hours', { count: hr });
  const days = Math.floor(hr / 24);
  if (days < 30) return t('topup_review.relative.days', { count: days });
  const months = Math.floor(days / 30);
  if (months < 12) return t('topup_review.relative.months', { count: months });
  return new Date(iso).toLocaleDateString();
}

function originalAmountLabel(amount: number, currency: string): string {
  // amount is in minor units of the original currency (per Phase 3.2 contract).
  // We render as decimal: divide by 100, prefix currency code.
  const major = (amount / 100).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return `${major} ${currency}`;
}

// Phase 8.x Debug B3 — `statusPillClass` removed in favour of the canonical
// `<BillingStatusBadge>` (apps/admin/src/components/billing/BillingStatusBadge.tsx)
// which applies the soft-tint+ring+dot visual language across the admin app.

function tabPillClass(active: boolean, key: string): string {
  if (active) return 'bg-pw-primary text-white border-transparent';
  // Inactive — colored border + colored label, white background.
  switch (key) {
    case 'all':
      return 'bg-pw-bg text-pw-primary border-pw-primary/40';
    case 'pending':
      return 'bg-pw-bg text-amber-600 dark:text-amber-300 border-amber-300';
    case 'approved':
      return 'bg-pw-bg text-emerald-600 dark:text-emerald-300 border-emerald-300';
    case 'rejected':
      return 'bg-pw-bg text-red-600 dark:text-red-300 border-red-300';
    case 'flagged':
      return 'bg-pw-bg text-rose-600 dark:text-rose-300 border-rose-300';
    default:
      return 'bg-pw-bg text-pw-text-muted border-pw-border';
  }
}

// Reject-reason enum codes that map to the modal dropdown labels
// (topup_review.modal.rejectReason.*). Anything else stored in reject_reason
// is admin free-text (or a legacy value) → shown verbatim.
const TOPUP_REJECT_REASON_CODES = new Set<string>([
  'wrong_amount',
  'wrong_memo',
  'unknown_sender',
  'duplicate',
  'suspicious',
  'missing_proof',
  'other',
]);

/** Map a stored reject_reason to human text matching the reject modal. */
function formatRejectReason(
  t: (key: string) => string,
  reason: string | null | undefined,
): string {
  if (!reason) return '—';
  if (TOPUP_REJECT_REASON_CODES.has(reason)) {
    return t(`topup_review.modal.rejectReason.${reason}`);
  }
  if (reason === 'cancelled_by_merchant') {
    return t('topup_review.reviews.cancelledByMerchant');
  }
  return reason;
}

function envelopeMessage(payload: unknown): string | null {
  if (
    payload &&
    typeof payload === 'object' &&
    'error' in payload &&
    (payload as ErrorEnvelope).error?.message
  ) {
    return (payload as ErrorEnvelope).error?.message ?? null;
  }
  return null;
}

// Narrow the openapi-fetch client to the verbs we actually use here. Keeps
// types tractable until shared/api/types.generated.ts is real.
type RawClient = {
  GET: (
    path: string,
    init?: { headers?: Record<string, string> },
  ) => Promise<{ data?: unknown; error?: unknown; response: Response }>;
  POST: (
    path: string,
    init: { body?: unknown; headers?: Record<string, string> },
  ) => Promise<{ data?: unknown; error?: unknown; response: Response }>;
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function TopupQueuePage(): JSX.Element {
  const { toast } = useToast();
  const { t } = useTranslation('finance');
  const [searchParams, setSearchParams] = useSearchParams();
  const [rows, setRows] = useState<AdminTopupRequestItem[]>([]);
  // D-69 (amend #3): true total from BE meta.total so numbered-page nav shows
  // the full count (not just the visible-window length).
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [refreshTick, setRefreshTick] = useState(0);
  // MerchantPicker trigger label — survives URL hydration so the picker
  // shows the picked merchant name on refresh.
  const [merchantLabel, setMerchantLabel] = useState<string | null>(null);

  // Drawer + action state.
  const [activeId, setActiveId] = useState<string | null>(null);
  const [detail, setDetail] = useState<TopupRequestDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [rejectInternalNote, setRejectInternalNote] = useState('');
  const [rejectFormError, setRejectFormError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [confirmApprove, setConfirmApprove] = useState(false);
  const [approveInternalNote, setApproveInternalNote] = useState('');
  // Issue 5b — tier-1 force-override reason input (only shown when the
  // active row is tier-1). BE requires min 10 chars.
  const [forceOverrideReason, setForceOverrideReason] = useState('');
  const [forceOverrideError, setForceOverrideError] = useState<string | null>(null);
  const [highlightId, setHighlightId] = useState<string | null>(null);

  // Read URL state.
  const tab = (searchParams.get('tab') as
    | 'all'
    | 'pending'
    | 'approved'
    | 'rejected'
    | 'flagged'
    | null) ?? 'pending';
  const filterMerchant = searchParams.get('merchant') ?? '';
  const filterMerchantQ = searchParams.get('q') ?? '';
  const filterGateway = searchParams.get('gateway') ?? '';
  const filterDateFrom = searchParams.get('date_from') ?? '';
  const filterDateTo = searchParams.get('date_to') ?? '';
  // Phase 8.2 audit 2026-05-23 — URL-synced page + pageSize per /stores
  // standard. Live values clamped to BE allowlist (1..100); default 25 matches
  // the PageListFooter pageSizeChoices [10, 25, 50, 100].
  const page = Math.max(1, Number.parseInt(searchParams.get('page') ?? '1', 10) || 1);
  const rawPageSize = Number.parseInt(searchParams.get('pageSize') ?? '25', 10);
  const pageSize = Number.isFinite(rawPageSize) && rawPageSize > 0
    ? Math.min(100, Math.max(10, rawPageSize))
    : 25;

  // Build query string for the list endpoint based on URL state.
  const listQueryString = useMemo(() => {
    const params = new URLSearchParams();
    if (tab === 'flagged') {
      params.set('flagged', 'true');
    } else if (tab !== 'all') {
      params.set('status', tab);
    }
    if (filterMerchant) params.set('merchant', filterMerchant);
    if (filterMerchantQ) params.set('q', filterMerchantQ);
    if (filterGateway) params.set('gateway', filterGateway);
    if (filterDateFrom) params.set('date_from', filterDateFrom);
    if (filterDateTo) params.set('date_to', filterDateTo);
    params.set('page', String(page));
    params.set('limit', String(pageSize));
    return params.toString();
  }, [tab, filterMerchant, filterMerchantQ, filterGateway, filterDateFrom, filterDateTo, page, pageSize]);

  const fetchRows = useCallback(async (): Promise<void> => {
    setLoading(true);
    try {
      const client = apiClient() as unknown as RawClient;
      const { data, error, response } = await client.GET(
        `/api/v1/admin/topup-requests?${listQueryString}`,
      );
      if (error || !response.ok) {
        toast({
          title: t('topup_review.queue.loadFailed'),
          description: envelopeMessage(error) ?? t('errors.requestFailed', { status: response.status }),
          variant: 'error',
        });
        setRows([]);
        return;
      }
      const payload = data as AdminTopupListResponse & {
        meta?: { total?: number };
      };
      // FIFO oldest-first.
      const sorted = [...(payload.data ?? [])].sort(
        (a, b) =>
          new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
      );
      setRows(sorted);
      // D-69: prefer BE meta.total; fall back to window length if absent.
      setTotal(payload.meta?.total ?? sorted.length);
    } catch (err) {
      toast({
        title: t('topup_review.networkError'),
        description: err instanceof Error ? err.message : t('topup_review.unknown'),
        variant: 'error',
      });
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [listQueryString, toast, t]);

  useEffect(() => {
    void fetchRows();
  }, [fetchRows, refreshTick]);

  // ---- URL helpers ----

  function updateParam(key: string, value: string): void {
    const next = new URLSearchParams(searchParams);
    if (value) next.set(key, value);
    else next.delete(key);
    setSearchParams(next, { replace: true });
  }

  function setTab(key: string): void {
    const next = new URLSearchParams(searchParams);
    if (key === 'pending') next.delete('tab');
    else next.set('tab', key);
    // Tab switch resets paging to page 1.
    next.delete('page');
    setSearchParams(next, { replace: true });
  }

  function setPage(nextPage: number): void {
    const next = new URLSearchParams(searchParams);
    if (nextPage <= 1) next.delete('page');
    else next.set('page', String(nextPage));
    setSearchParams(next, { replace: true });
  }

  function handlePageSizeChange(size: number): void {
    const next = new URLSearchParams(searchParams);
    next.set('pageSize', String(size));
    next.delete('page'); // reset to page 1
    setSearchParams(next, { replace: true });
  }

  // ---- Drawer detail fetch ----

  const fetchDetail = useCallback(
    async (id: string): Promise<void> => {
      setDetailLoading(true);
      try {
        const client = apiClient() as unknown as RawClient;
        const { data, error, response } = await client.GET(
          `/api/v1/admin/topup-requests/${encodeURIComponent(id)}`,
        );
        if (error || !response.ok) {
          toast({
            title: t('topup_review.queue.detailLoadFailed'),
            description: envelopeMessage(error) ?? t('errors.requestFailed', { status: response.status }),
            variant: 'error',
          });
          setDetail(null);
          return;
        }
        const payload = data as { data?: TopupRequestDetail } | TopupRequestDetail;
        const inner =
          payload && typeof payload === 'object' && 'data' in payload && payload.data
            ? payload.data
            : (payload as TopupRequestDetail);
        setDetail(inner);
      } catch (err) {
        toast({
          title: t('topup_review.networkError'),
          description: err instanceof Error ? err.message : t('topup_review.unknown'),
          variant: 'error',
        });
        setDetail(null);
      } finally {
        setDetailLoading(false);
      }
    },
    [toast, t],
  );

  function openDrawer(row: AdminTopupRequestItem): void {
    setActiveId(row.id);
    setDetail(null);
    setRejectReason('');
    setRejectInternalNote('');
    setRejectFormError(null);
    setApproveInternalNote('');
    void fetchDetail(row.id);
  }

  function closeDrawer(): void {
    setActiveId(null);
    setDetail(null);
    setRejectFormError(null);
  }

  // ---- Race-loser handler ----

  function isRaceLoser(status: number): boolean {
    return status === 409 || status === 410;
  }

  function showRaceLoserToast(): void {
    toast({
      title: t('topup_review.raceLoser.title'),
      description: t('topup_review.raceLoser.description'),
      variant: 'warning',
    });
  }

  // ---- Auto-highlight next pending after action ----

  function highlightNextPending(currentId: string): void {
    const idx = rows.findIndex((r) => r.id === currentId);
    if (idx === -1) return;
    const next = rows.slice(idx + 1).find((r) => r.status === 'pending');
    if (next) setHighlightId(next.id);
  }

  // ---- Approve action ----

  async function handleApprove(): Promise<void> {
    if (!activeId) return;
    setSubmitting(true);
    try {
      const client = apiClient() as unknown as RawClient;
      const idempotencyKey =
        typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
          ? crypto.randomUUID()
          : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
      const isTier1 = detail?.tier === 'tier1';
      // Issue 5b: tier-1 requires force_override + reason ≥10 chars.
      // Form-validate before sending to avoid useless round-trip + clearer UX.
      if (isTier1 && forceOverrideReason.trim().length < 10) {
        setForceOverrideError(t('topup_review.queue.forceOverrideMin'));
        setSubmitting(false);
        return;
      }
      setForceOverrideError(null);
      const body: {
        internal_note?: string;
        force_override?: boolean;
        override_reason?: string;
      } = {};
      if (approveInternalNote.trim()) body.internal_note = approveInternalNote.trim();
      if (isTier1) {
        body.force_override = true;
        body.override_reason = forceOverrideReason.trim();
      }
      const { error, response } = await client.POST(
        `/api/v1/admin/topup-requests/${encodeURIComponent(activeId)}/approve`,
        {
          body,
          headers: { 'Idempotency-Key': idempotencyKey },
        },
      );
      if (!response.ok) {
        if (isRaceLoser(response.status)) {
          showRaceLoserToast();
          setConfirmApprove(false);
          closeDrawer();
          setRefreshTick((t) => t + 1);
          return;
        }
        toast({
          title: t('topup_review.queue.approveFailed'),
          description: envelopeMessage(error) ?? t('errors.requestFailed', { status: response.status }),
          variant: 'error',
        });
        return;
      }
      toast({
        title: t('topup_review.queue.approveSuccess'),
        description: t('topup_review.queue.approveSuccessDesc'),
        variant: 'success',
      });
      highlightNextPending(activeId);
      setConfirmApprove(false);
      closeDrawer();
      setRefreshTick((t) => t + 1);
    } catch (err) {
      toast({
        title: t('topup_review.networkError'),
        description: err instanceof Error ? err.message : t('topup_review.unknown'),
        variant: 'error',
      });
    } finally {
      setSubmitting(false);
    }
  }

  // ---- Reject action ----

  async function handleReject(): Promise<void> {
    if (!activeId) return;
    // Form-level validation (: reason required).
    if (rejectReason.trim().length < 3) {
      setRejectFormError(t('topup_review.queue.rejectReasonMin'));
      return;
    }
    setRejectFormError(null);
    setSubmitting(true);
    try {
      const client = apiClient() as unknown as RawClient;
      const idempotencyKey =
        typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
          ? crypto.randomUUID()
          : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
      const body: { reason: string; internal_note?: string } = {
        reason: rejectReason.trim(),
      };
      if (rejectInternalNote.trim()) {
        body.internal_note = rejectInternalNote.trim();
      }
      const { error, response } = await client.POST(
        `/api/v1/admin/topup-requests/${encodeURIComponent(activeId)}/reject`,
        {
          body,
          headers: { 'Idempotency-Key': idempotencyKey },
        },
      );
      if (!response.ok) {
        if (isRaceLoser(response.status)) {
          showRaceLoserToast();
          closeDrawer();
          setRefreshTick((t) => t + 1);
          return;
        }
        toast({
          title: t('topup_review.queue.rejectFailed'),
          description: envelopeMessage(error) ?? t('errors.requestFailed', { status: response.status }),
          variant: 'error',
        });
        return;
      }
      toast({
        title: t('topup_review.queue.rejectSuccess'),
        description: t('topup_review.queue.rejectSuccessDesc'),
        variant: 'success',
      });
      highlightNextPending(activeId);
      closeDrawer();
      setRefreshTick((t) => t + 1);
    } catch (err) {
      toast({
        title: t('topup_review.networkError'),
        description: err instanceof Error ? err.message : t('topup_review.unknown'),
        variant: 'error',
      });
    } finally {
      setSubmitting(false);
    }
  }

  // ---- Flag toggle ----

  async function handleFlagToggle(): Promise<void> {
    if (!detail) return;
    setSubmitting(true);
    try {
      const client = apiClient() as unknown as RawClient;
      const idempotencyKey =
        typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
          ? crypto.randomUUID()
          : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
      const next = !detail.flagged_suspicious;
      const { error, response } = await client.POST(
        `/api/v1/admin/merchants/${encodeURIComponent(
          detail.merchant_id,
        )}/topup-requests/${encodeURIComponent(detail.id)}/flag-suspicious`,
        {
          body: { flagged: next },
          headers: { 'Idempotency-Key': idempotencyKey },
        },
      );
      if (!response.ok) {
        toast({
          title: t('topup_review.queue.flagFailed'),
          description: envelopeMessage(error) ?? t('errors.requestFailed', { status: response.status }),
          variant: 'error',
        });
        return;
      }
      toast({
        title: next ? t('topup_review.queue.flagMarked') : t('topup_review.queue.flagCleared'),
        description: t('topup_review.queue.flagDesc'),
        variant: 'success',
      });
      // Refresh detail + list.
      void fetchDetail(detail.id);
      setRefreshTick((t) => t + 1);
    } catch (err) {
      toast({
        title: t('topup_review.networkError'),
        description: err instanceof Error ? err.message : t('topup_review.unknown'),
        variant: 'error',
      });
    } finally {
      setSubmitting(false);
    }
  }

  // ---- Export CSV (opens new tab per acceptance criteria) ----

  function handleExport(): void {
    const params = new URLSearchParams();
    if (tab === 'flagged') params.set('flagged', 'true');
    else if (tab !== 'all') params.set('status', tab);
    if (filterMerchant) params.set('merchant', filterMerchant);
    if (filterGateway) params.set('gateway', filterGateway);
    if (filterDateFrom) params.set('date_from', filterDateFrom);
    if (filterDateTo) params.set('date_to', filterDateTo);
    // F-11 (review 3.2): prefix with resolveApiBaseUrl() so the export hits
    // api-v3, not the admin-v3 SPA origin (which returns index.html/404).
    // Mirrors the receipt-link fix below.
    const url = `${resolveApiBaseUrl()}/api/v1/admin/topup-requests/export${
      params.toString() ? `?${params.toString()}` : ''
    }`;
    window.open(url, '_blank', 'noopener,noreferrer');
  }

  // ---- Counts (per tab) ----

  // Tab counts: prefer authoritative `/counts` endpoint (Issue 5c fix —
  // debug-bill iter 13, 2026-05-04) which returns all 5 tab counts in one
  // round-trip. Fallback to per-tab `tabCountCache` (populated as user
  // clicks tabs) if /counts hasn't returned yet. `totalForCurrentTab` is
  // still the rows.length on screen so the "X in queue" header stays in
  // sync with the visible page.
  const totalForCurrentTab = rows.length;
  const [tabCountCache, setTabCountCache] = useState<Record<string, number>>({});
  const [authoritativeCounts, setAuthoritativeCounts] =
    useState<Record<string, number> | null>(null);
  useEffect(() => {
    setTabCountCache((prev) => ({ ...prev, [tab]: rows.length }));
  }, [tab, rows.length]);
  // Fetch authoritative counts whenever filters (excluding tab itself) change.
  useEffect(() => {
    let cancelled = false;
    void (async (): Promise<void> => {
      try {
        const params = new URLSearchParams();
        if (filterMerchant) params.set('merchant', filterMerchant);
        if (filterGateway) params.set('gateway', filterGateway);
        if (filterDateFrom) params.set('date_from', filterDateFrom);
        if (filterDateTo) params.set('date_to', filterDateTo);
        const client = apiClient() as unknown as RawClient;
        const { data, response } = await client.GET(
          `/api/v1/admin/topup-requests/counts?${params.toString()}`,
        );
        if (cancelled || !response.ok) return;
        const body = data as
          | { data?: Record<string, number> }
          | undefined;
        const inner = body?.data;
        if (inner && typeof inner === 'object') {
          setAuthoritativeCounts(inner);
        }
      } catch {
        // Non-fatal — fall back to tabCountCache. Already-displayed counts
        // remain visible (last-known values).
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [filterMerchant, filterGateway, filterDateFrom, filterDateTo, refreshTick]);

  // ---- Auto-focus highlighted row after action ----

  const highlightedRowRef = useRef<HTMLTableRowElement | null>(null);
  useEffect(() => {
    if (highlightId && highlightedRowRef.current) {
      highlightedRowRef.current.focus();
    }
  }, [highlightId, rows]);

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <div className="flex flex-col gap-6 p-6">
      {/* Header — title + queue pill + Export CSV */}
      <PageHeader
        title={t('topup_review.queue.title')}
        subtitle={t('topup_review.queue.subtitle')}
        actions={
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 rounded-md bg-pw-primary/10 px-3 py-2 text-sm font-medium text-pw-primary-strong">
              <ClockIcon />
              <span>
                {t('topup_review.queue.queuePill', { count: totalForCurrentTab })}
              </span>
            </div>
            <Button variant="secondary" onClick={handleExport}>
              <DownloadIcon />
              <span className="ml-2">{t('topup_review.queue.exportCsv')}</span>
            </Button>
          </div>
        }
      />

      {/* Quick filter tabs */}
      <div className="flex flex-wrap items-center gap-3">
        {QUICK_TABS.map((tabItem) => {
          const active = tab === tabItem.key;
          const cached = tabCountCache[tabItem.key];
          // Prefer authoritative count if available; else fallback to per-tab
          // cached fetch count; else current visible page count for active tab.
          const authoritative = authoritativeCounts?.[tabItem.key];
          const count =
            authoritative !== undefined
              ? authoritative
              : active
                ? totalForCurrentTab
                : cached !== undefined
                  ? cached
                  : '—';
          return (
            <button
              key={tabItem.key}
              type="button"
              onClick={() => setTab(tabItem.key)}
              aria-pressed={active}
              className={cn(
                'inline-flex items-center gap-2 rounded-md border px-4 py-2 text-sm font-medium transition-colors',
                tabPillClass(active, tabItem.key),
              )}
            >
              <span>{t(tabItem.labelKey)}</span>
              <span className="text-xs opacity-90">({count})</span>
            </button>
          );
        })}
      </div>

      {/* Filter bar — merchant, gateway, date range. Cluster B audit
          (2026-05-23): replaced "Merchant search" (q=name/email free-text)
          + "Merchant ID" (24-hex ObjectId paste) with a single
          MerchantPicker. The picker's modal already debounces a
          name/email/id search via /admin/merchants?q. */}
      <Card>
        <CardContent className="flex flex-wrap items-end gap-3 py-4">
          <label className="flex flex-col text-xs font-medium text-pw-text-muted">
            {t('topup_review.queue.filter.merchant')}
            <div className="mt-1 w-64">
              <MerchantPicker
                value={filterMerchant || null}
                valueLabel={merchantLabel}
                onChange={(id, label) => {
                  setMerchantLabel(label);
                  // Drop the legacy `q` free-text — picker IS the search.
                  const next = new URLSearchParams(searchParams);
                  if (id) next.set('merchant', id);
                  else next.delete('merchant');
                  next.delete('q');
                  setSearchParams(next, { replace: true });
                }}
                placeholder={t('topup_review.queue.filter.allMerchants')}
                testId="topup-queue-merchant-filter"
              />
            </div>
          </label>
          <label className="flex flex-col text-xs font-medium text-pw-text-muted">
            {t('topup_review.queue.filter.gateway')}
            <Select
              value={filterGateway === '' ? ALL_VALUE : filterGateway}
              onValueChange={(value) =>
                updateParam('gateway', value === ALL_VALUE ? '' : value)
              }
            >
              <SelectTrigger className="mt-1 w-40">
                <SelectValue placeholder={t('topup_review.queue.filter.allGateways')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL_VALUE}>
                  {t('topup_review.queue.filter.allGateways')}
                </SelectItem>
                {GATEWAY_OPTIONS.map((g) => (
                  <SelectItem key={g} value={g}>
                    {g}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </label>
          <label className="flex flex-col text-xs font-medium text-pw-text-muted">
            {t('topup_review.queue.filter.from')}
            <Input
              type="date"
              className="mt-1 w-44"
              value={filterDateFrom ? filterDateFrom.slice(0, 10) : ''}
              onChange={(e) =>
                updateParam(
                  'date_from',
                  e.target.value ? `${e.target.value}T00:00:00.000Z` : '',
                )
              }
            />
          </label>
          <label className="flex flex-col text-xs font-medium text-pw-text-muted">
            {t('topup_review.queue.filter.to')}
            <Input
              type="date"
              className="mt-1 w-44"
              value={filterDateTo ? filterDateTo.slice(0, 10) : ''}
              onChange={(e) =>
                updateParam(
                  'date_to',
                  e.target.value ? `${e.target.value}T23:59:59.999Z` : '',
                )
              }
            />
          </label>
          {(filterMerchant ||
            filterMerchantQ ||
            filterGateway ||
            filterDateFrom ||
            filterDateTo) && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                const next = new URLSearchParams(searchParams);
                next.delete('merchant');
                next.delete('q');
                next.delete('gateway');
                next.delete('date_from');
                next.delete('date_to');
                setSearchParams(next, { replace: true });
              }}
            >
              {t('topup_review.queue.filter.clear')}
            </Button>
          )}
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center p-12 text-sm text-pw-text-muted">
              {t('topup_review.queue.loading')}
            </div>
          ) : rows.length === 0 ? (
            <EmptyState />
          ) : (
            <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-amber-50 dark:bg-amber-950/40">
                <tr className="text-left text-xs font-medium uppercase tracking-wide text-pw-text-muted">
                  {/* Issue 5a fix (debug-bill iter 13, 2026-05-04): expose
                      request ID column so ops can reference rows in support
                      tickets and BE logs. Mono 10px to keep column tight. */}
                  <th className="px-4 py-3">{t('topup_review.queue.column.id')}</th>
                  <th className="px-4 py-3">{t('topup_review.queue.column.merchant')}</th>
                  <th className="px-4 py-3">{t('topup_review.queue.column.amount')}</th>
                  <th className="px-4 py-3">{t('topup_review.queue.column.gateway')}</th>
                  <th className="px-4 py-3">{t('topup_review.queue.column.txId')}</th>
                  <th className="px-4 py-3">{t('topup_review.queue.column.date')}</th>
                  <th className="px-4 py-3">{t('topup_review.queue.column.status')}</th>
                  <th className="px-4 py-3 text-right">{t('topup_review.queue.column.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => {
                  // N-1 (review 3.2 broaden): mirror the detail drawer —
                  // show "—" when the locked USD is not set yet (manual/unlocked
                  // topups) instead of a misleading "$0.00". Original-amount
                  // subtext below still carries the real figure.
                  const dual =
                    row.usd_amount != null
                      ? formatDual(row.usd_amount, 'USD', {})
                      : { primary: '—', secondary: '' };
                  const isHighlighted = highlightId === row.id;
                  return (
                    <tr
                      key={row.id}
                      ref={isHighlighted ? highlightedRowRef : undefined}
                      tabIndex={isHighlighted ? 0 : -1}
                      onClick={() => openDrawer(row)}
                      className={cn(
                        'cursor-pointer border-t border-pw-border transition-colors hover:bg-pw-surface',
                        isHighlighted &&
                          'bg-amber-50 dark:bg-amber-950/40 ring-2 ring-pw-primary/40 outline-none',
                      )}
                    >
                      <td className="px-4 py-4 align-top">
                        <span
                          className="font-mono text-[10px] text-pw-text-muted"
                          title={row.id}
                        >
                          TR-…{row.id.slice(-6)}
                        </span>
                      </td>
                      <td className="px-4 py-4">
                        <div
                          className="font-medium text-pw-text"
                          title={row.merchant?.id}
                        >
                          {row.merchant?.name ?? '—'}
                        </div>
                        <div className="text-xs text-pw-text-muted">
                          {row.merchant?.email ?? ''}
                        </div>
                      </td>
                      <td className="px-4 py-4 align-top">
                        <div className="font-medium text-pw-text">
                          {dual.primary}
                        </div>
                        <div className="text-xs text-pw-text-muted">
                          {originalAmountLabel(
                            row.original_amount,
                            row.original_currency,
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-4 align-top capitalize">
                        {row.gateway}
                      </td>
                      <td className="px-4 py-4 align-top font-mono text-xs text-pw-text-muted">
                        {/* F-10 (review 3.2): the list payload carries no real
                            gateway transaction_id, so render a neutral dash
                            instead of slicing the internal request _id — a
                            fabricated code that reads like a real tx id in a
                            financial queue. Open the detail drawer for the tx id. */}
                        —
                      </td>
                      <td className="px-4 py-4 align-top text-pw-text-muted">
                        {relativeTime(row.created_at, t)}
                      </td>
                      <td className="px-4 py-4 align-top">
                        {/* Bug 8 (debug-bill iter 6, 2026-05-04): keep the real
                            status visible with a flag glyph appended, never
                            replace status with "Flagged". */}
                        <BillingStatusBadge
                          status={row.status}
                          flagged={row.flagged_suspicious}
                          title={
                            row.flagged_suspicious
                              ? t('topup_review.queue.flaggedTitle')
                              : undefined
                          }
                          label={
                            row.flagged_suspicious
                              ? `${row.status[0]?.toUpperCase() ?? ''}${row.status.slice(1)} ⚠`
                              : undefined
                          }
                        />
                      </td>
                      <td
                        className="px-4 py-4 align-top text-right"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {row.status === 'pending' ? (
                          <div className="inline-flex items-center gap-2">
                            {/* Tier 1 (PayPal/Stripe/Payoneer/LianLian) auto-approved by webhook
                                per CONTEXT.md P3.D-15. Manual approve disallowed by BE
                                (TIER_1_AUTO_APPROVED 422). Show Review-only for visibility. */}
                            {row.tier === 'tier2' ? (
                              <Button
                                variant="default"
                                size="sm"
                                onClick={() => {
                                  setActiveId(row.id);
                                  void fetchDetail(row.id);
                                  setApproveInternalNote('');
                                  setConfirmApprove(true);
                                }}
                              >
                                {t('topup_review.queue.approve')}
                              </Button>
                            ) : (
                              <span className="text-xs text-pw-text-muted px-2">
                                {t('topup_review.queue.autoWebhook')}
                              </span>
                            )}
                            <Button
                              variant="destructive"
                              size="sm"
                              onClick={() => openDrawer(row)}
                            >
                              {t('topup_review.queue.reject')}
                            </Button>
                          </div>
                        ) : (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openDrawer(row)}
                          >
                            {t('topup_review.queue.review')}
                          </Button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            </div>
          )}
          {/* Pagination — canonical /stores footer (Rows-per-page + Total +
              numbered) per audit 2026-05-23 Cluster A. BE list endpoint is
              cursor-based and does not return a total count; we surface the
              visible-page count so the Rows selector remains reachable and
              page navigation drives the BE page/limit query params. */}
          {rows.length > 0 ? (
            <div className="border-t border-pw-border px-6 py-3">
              <PageListFooter
                page={page}
                pageSize={pageSize}
                total={total}
                loading={loading}
                onPageChange={setPage}
                onPageSizeChange={handlePageSizeChange}
                testId="topup-queue-footer"
              />
            </div>
          ) : null}
        </CardContent>
      </Card>

      {/* Side drawer — slide-in from right */}
      {activeId !== null && (
        <DetailDrawer
          detail={detail}
          loading={detailLoading}
          submitting={submitting}
          rejectReason={rejectReason}
          rejectInternalNote={rejectInternalNote}
          rejectFormError={rejectFormError}
          onRejectReasonChange={setRejectReason}
          onRejectInternalNoteChange={setRejectInternalNote}
          onClose={closeDrawer}
          onApprove={() => {
            setApproveInternalNote('');
            setForceOverrideReason('');
            setForceOverrideError(null);
            setConfirmApprove(true);
          }}
          onReject={handleReject}
          onFlagToggle={handleFlagToggle}
        />
      )}

      {/* Approve confirm dialog */}
      {confirmApprove && (
        <ConfirmDialog
          open={confirmApprove}
          onOpenChange={(o) => {
            if (!o) setConfirmApprove(false);
          }}
          type="neutral"
          title={
            detail?.usd_amount != null
              ? t('topup_review.queue.confirmApproveTitleAmount', {
                  amount: formatDual(detail.usd_amount, 'USD', {}).primary,
                })
              : t('topup_review.queue.confirmApproveTitle')
          }
          description={
            <div className="space-y-3 text-left">
              {detail?.tier === 'tier1' ? (
                <div className="rounded-md border border-rose-200 dark:border-rose-800 bg-rose-50 dark:bg-rose-950/40 p-3 text-xs text-rose-800 dark:text-rose-300">
                  <strong>{t('topup_review.queue.forceApproveTier1Bold')}</strong>{' '}
                  {t('topup_review.queue.forceApproveTier1Body')}
                </div>
              ) : null}
              <p className="text-sm text-pw-text-muted">
                {t('topup_review.queue.confirmApproveBody')}
              </p>
              {detail?.tier === 'tier1' ? (
                <div>
                  <textarea
                    className="block w-full min-h-[80px] rounded-md border border-pw-border bg-pw-bg p-2 text-sm focus:border-pw-primary focus:outline-none focus:ring-2 focus:ring-pw-primary/20"
                    placeholder={t('topup_review.queue.forceOverridePlaceholder')}
                    value={forceOverrideReason}
                    onChange={(e) => setForceOverrideReason(e.target.value)}
                    aria-invalid={!!forceOverrideError}
                    data-testid="topup-force-override-reason"
                  />
                  {forceOverrideError ? (
                    <p className="mt-1 text-xs text-red-600 dark:text-red-300">{forceOverrideError}</p>
                  ) : null}
                </div>
              ) : null}
              <Input
                placeholder={t('topup_review.queue.internalNotePlaceholder')}
                value={approveInternalNote}
                onChange={(e) => setApproveInternalNote(e.target.value)}
              />
            </div>
          }
          confirmLabel={detail?.tier === 'tier1' ? t('topup_review.queue.forceApprove') : t('topup_review.queue.approve')}
          // dbg-20260725 (#2631 bước 8): ConfirmDialog defaults cancelLabel to
          // the literal 'Cancel' (packages/ui has no i18n), so the cancel button
          // rendered English even with locale=vi. Pass the translated label.
          cancelLabel={t('topup_review.queue.cancel')}
          loading={submitting}
          onConfirm={handleApprove}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// DetailDrawer — local-only, phase-specific. Side panel slides in from right.
// Contains receipt link, flag toggle, reject form, and footer actions.
// ---------------------------------------------------------------------------

function DetailDrawer(props: {
  detail: TopupRequestDetail | null;
  loading: boolean;
  submitting: boolean;
  rejectReason: string;
  rejectInternalNote: string;
  rejectFormError: string | null;
  onRejectReasonChange: (v: string) => void;
  onRejectInternalNoteChange: (v: string) => void;
  onClose: () => void;
  onApprove: () => void;
  onReject: () => void | Promise<void>;
  onFlagToggle: () => void | Promise<void>;
}): JSX.Element {
  const {
    detail,
    loading,
    submitting,
    rejectReason,
    rejectInternalNote,
    rejectFormError,
    onRejectReasonChange,
    onRejectInternalNoteChange,
    onClose,
    onApprove,
    onReject,
    onFlagToggle,
  } = props;
  const { t } = useTranslation('finance');

  const dual =
    detail?.usd_amount != null
      ? formatDual(detail.usd_amount, 'USD', {})
      : { primary: '—', secondary: '' };
  const isPending = detail?.status === 'pending';

  return (
    <div
      className="fixed inset-0 z-40 flex justify-end"
      role="dialog"
      aria-modal="true"
    >
      {/* Backdrop */}
      <button
        type="button"
        aria-label={t('topup_review.drawer.closeDrawer')}
        className="absolute inset-0 bg-black/40" /* dark-ok: modal backdrop scrim, theme-agnostic (P999.11.D-04) */
        onClick={onClose}
      />
      {/* Panel */}
      <aside className="relative z-50 flex h-full w-full max-w-md flex-col bg-pw-bg shadow-xl">
        {/* Header */}
        <div className="flex items-start justify-between border-b border-pw-border p-5">
          <div className="min-w-0">
            <h2 className="truncate text-lg font-semibold text-pw-text">
              {detail?.merchant?.name ?? t('topup_review.drawer.fallbackName')}
            </h2>
            <p className="truncate text-xs text-pw-text-muted">
              {detail?.merchant?.email ?? ''}
            </p>
          </div>
          <button
            type="button"
            aria-label={t('topup_review.drawer.close')}
            className="rounded-md p-1.5 text-pw-text-muted transition-colors hover:bg-pw-surface hover:text-pw-text"
            onClick={onClose}
          >
            <CloseIcon />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-5">
          {loading || !detail ? (
            <div className="flex items-center justify-center py-12 text-sm text-pw-text-muted">
              {t('topup_review.drawer.loading')}
            </div>
          ) : (
            <div className="flex flex-col gap-5">
              {/* Status pill + flag toggle */}
              <div className="flex items-center justify-between gap-3">
                <BillingStatusBadge
                  status={detail.status}
                  flagged={detail.flagged_suspicious}
                  label={detail.flagged_suspicious ? t('topup_review.drawer.flagged') : undefined}
                />
                <label className="inline-flex cursor-pointer items-center gap-2 text-sm text-pw-text">
                  <input
                    type="checkbox"
                    checked={detail.flagged_suspicious}
                    onChange={() => void onFlagToggle()}
                    disabled={submitting}
                    className="h-4 w-4 rounded border-pw-border accent-pw-primary"
                  />
                  {t('topup_review.drawer.flagAsSuspicious')}
                </label>
              </div>

              {/* Amount card */}
              <div className="rounded-lg border border-pw-border bg-pw-surface p-4">
                <div className="text-xs uppercase tracking-wide text-pw-text-muted">
                  {t('topup_review.drawer.amount')}
                </div>
                <div className="mt-1 text-2xl font-semibold text-pw-text">
                  {dual.primary}
                </div>
                {dual.secondary && (
                  <div className="text-sm text-pw-text-muted">
                    {dual.secondary}
                  </div>
                )}
                <div className="mt-2 text-xs text-pw-text-muted">
                  {t('topup_review.drawer.original')}{' '}
                  {originalAmountLabel(
                    detail.original_amount,
                    detail.original_currency,
                  )}
                  {detail.fx_rate_locked != null
                    ? t('topup_review.drawer.fxLocked', {
                        rate: (detail.fx_rate_locked / 1_000_000).toFixed(6),
                      })
                    : ''}
                </div>
              </div>

              {/* Gateway + tier */}
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <div className="text-xs uppercase tracking-wide text-pw-text-muted">
                    {t('topup_review.drawer.gateway')}
                  </div>
                  <div className="mt-1 font-medium capitalize text-pw-text">
                    {detail.gateway}
                  </div>
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wide text-pw-text-muted">
                    {t('topup_review.drawer.tier')}
                  </div>
                  <div className="mt-1 font-medium uppercase text-pw-text">
                    {detail.tier}
                  </div>
                </div>
              </div>

              {/* Tx ID */}
              {detail.transaction_id && (
                <div>
                  <div className="text-xs uppercase tracking-wide text-pw-text-muted">
                    {t('topup_review.drawer.transactionId')}
                  </div>
                  <div className="mt-1 break-all font-mono text-xs text-pw-text">
                    {detail.transaction_id}
                  </div>
                </div>
              )}

              {/* Receipt */}
              {detail.receipt_file_id && (
                <div>
                  <div className="text-xs uppercase tracking-wide text-pw-text-muted">
                    {t('topup_review.drawer.receipt')}
                  </div>
                  <div className="mt-1 flex gap-3 text-sm">
                    {/* Bug 18 (debug-receipt iter 1, 2026-05-04): href was a
                        relative `/api/v1/files/...` URL — when admin SPA is
                        served from admin-v3.printway.work, clicking the link
                        hit admin-v3 (NOT api-v3) → SPA returned its index.html
                        + got 404 from FE router. Prefix with VITE_API_URL via
                        resolveApiBaseUrl() so link points at the correct
                        api-v3 origin. */}
                    <a
                      href={`${resolveApiBaseUrl()}/api/v1/files/${encodeURIComponent(detail.receipt_file_id)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-pw-primary-strong underline hover:text-pw-primary-hover"
                    >
                      {t('topup_review.drawer.openReceipt')}
                    </a>
                    <a
                      href={`${resolveApiBaseUrl()}/api/v1/files/${encodeURIComponent(detail.receipt_file_id)}?download=1`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-pw-primary-strong underline hover:text-pw-primary-hover"
                    >
                      {t('topup_review.drawer.download')}
                    </a>
                  </div>
                </div>
              )}

              {/* Note */}
              {detail.note && (
                <div>
                  <div className="text-xs uppercase tracking-wide text-pw-text-muted">
                    {t('topup_review.drawer.merchantNote')}
                  </div>
                  <div className="mt-1 whitespace-pre-wrap text-sm text-pw-text">
                    {detail.note}
                  </div>
                </div>
              )}

              {/* Created at */}
              <div>
                <div className="text-xs uppercase tracking-wide text-pw-text-muted">
                  {t('topup_review.drawer.createdAt')}
                </div>
                <div className="mt-1 text-sm text-pw-text">
                  {new Date(detail.created_at).toLocaleString()}
                </div>
              </div>

              {/* Approved/Rejected meta */}
              {detail.status === 'approved' && detail.approved_at && (
                <div>
                  <div className="text-xs uppercase tracking-wide text-pw-text-muted">
                    {t('topup_review.drawer.approvedAt')}
                  </div>
                  <div className="mt-1 text-sm text-pw-text">
                    {new Date(detail.approved_at).toLocaleString()}
                  </div>
                </div>
              )}
              {detail.status === 'rejected' && detail.rejected_reason && (
                <div>
                  <div className="text-xs uppercase tracking-wide text-pw-text-muted">
                    {t('topup_review.drawer.rejectedReason')}
                  </div>
                  <div className="mt-1 whitespace-pre-wrap text-sm text-pw-text">
                    {detail.rejected_reason}
                  </div>
                </div>
              )}

              {/* Reject form (only for pending) */}
              {isPending && (
                <div className="rounded-lg border border-pw-border bg-pw-bg p-4">
                  <div className="text-xs uppercase tracking-wide text-pw-text-muted">
                    {t('topup_review.drawer.rejectThis')}
                  </div>
                  <label className="mt-2 block text-sm font-medium text-pw-text">
                    {t('topup_review.drawer.reason')}{' '}
                    <span className="text-red-600 dark:text-red-300" aria-hidden="true">
                      *
                    </span>
                  </label>
                  <textarea
                    className={cn(
                      'mt-1 block h-20 w-full rounded-md border bg-pw-bg p-2 text-sm focus:outline-none focus:ring-2',
                      rejectFormError
                        ? 'border-red-500 focus:border-red-500 focus:ring-red-500/20'
                        : 'border-pw-border focus:border-pw-primary focus:ring-pw-primary/20',
                    )}
                    placeholder={t('topup_review.drawer.rejectReasonPlaceholder')}
                    value={rejectReason}
                    onChange={(e) => onRejectReasonChange(e.target.value)}
                    aria-invalid={!!rejectFormError}
                  />
                  {rejectFormError && (
                    <p className="mt-1 text-xs text-red-600 dark:text-red-300">
                      {rejectFormError}
                    </p>
                  )}
                  <label className="mt-3 block text-sm font-medium text-pw-text">
                    {t('topup_review.drawer.internalNote')}
                  </label>
                  <textarea
                    className="mt-1 block h-16 w-full rounded-md border border-pw-border bg-pw-bg p-2 text-sm focus:border-pw-primary focus:outline-none focus:ring-2 focus:ring-pw-primary/20"
                    placeholder={t('topup_review.drawer.internalNotePlaceholder')}
                    value={rejectInternalNote}
                    onChange={(e) =>
                      onRejectInternalNoteChange(e.target.value)
                    }
                  />
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-pw-border p-5">
          {detail && isPending ? (
            <div className="flex justify-end gap-3">
              <Button
                variant="destructive"
                onClick={() => void onReject()}
                disabled={submitting}
              >
                {t('topup_review.drawer.reject')}
              </Button>
              {detail.tier === 'tier2' ? (
                <Button
                  variant="default"
                  onClick={onApprove}
                  disabled={submitting}
                >
                  {t('topup_review.drawer.approve')}
                </Button>
              ) : (
                <>
                  {/* Issue 5b fix (debug-bill iter 13, 2026-05-04): tier-1
                      should auto-approve via webhook, but un-flag-pending
                      cases need a fallback. Show "Force approve" with
                      destructive variant + warning copy; opens dialog asking
                      reason ≥10 chars (audit log). */}
                  <span className="px-2 py-2 text-xs text-pw-text-muted">
                    {t('topup_review.drawer.tier1AutoWebhook')}
                  </span>
                  <Button
                    variant="destructive"
                    onClick={onApprove}
                    disabled={submitting}
                    data-testid="topup-force-approve-btn"
                    title={t('topup_review.drawer.forceApproveTitle')}
                  >
                    {t('topup_review.drawer.forceApprove')}
                  </Button>
                </>
              )}
            </div>
          ) : (
            <div className="flex justify-end">
              <Button variant="secondary" onClick={onClose}>
                {t('topup_review.drawer.close')}
              </Button>
            </div>
          )}
        </div>
      </aside>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Empty state
// ---------------------------------------------------------------------------

function EmptyState(): JSX.Element {
  const { t } = useTranslation('finance');
  return (
    <div className="flex flex-col items-center gap-3 px-6 py-16 text-center">
      <div
        aria-hidden="true"
        className="flex h-16 w-16 items-center justify-center rounded-full bg-pw-surface text-pw-text-muted"
      >
        <ClockIcon size={32} />
      </div>
      <h3 className="text-base font-semibold text-pw-text">
        {t('topup_review.queue.emptyTitle')}
      </h3>
      <p className="max-w-sm text-sm text-pw-text-muted">
        {t('topup_review.queue.emptyBody')}
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tiny icon set (Lucide-style outline) — avoid pulling new deps.
// ---------------------------------------------------------------------------

function ClockIcon({ size = 16 }: { size?: number }): JSX.Element {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
      width={size}
      height={size}
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="10" />
      <polyline points="12 6 12 12 16 14" />
    </svg>
  );
}

function DownloadIcon(): JSX.Element {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="h-4 w-4"
      aria-hidden="true"
    >
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="7 10 12 15 17 10" />
      <line x1="12" y1="15" x2="12" y2="3" />
    </svg>
  );
}

function CloseIcon(): JSX.Element {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="h-5 w-5"
      aria-hidden="true"
    >
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}

export default TopupQueuePage;

// ===========================================================================
// Phase 8.2 Wave 2 Task 06 — Topup Review queue (Finance billing tab)
// ===========================================================================
//
// New surface that consumes the Phase 8.2 Finance-side endpoints:
//   GET  /api/v1/admin/topups
//   POST /api/v1/admin/topups/:id/approve
//   POST /api/v1/admin/topups/:id/reject
//
// Per CONTEXT.md
//   • Billing tab structure (Topup Reviews tab).
//   • Branches on `verification_status`:
//                   `auto_verified` → inline 1-click approve (no modal)
//                   `manual_review` → opens TopupProofReviewModal
//   • Modal exposes 4 sections (IMAP/memo/screenshot/wallet).
//   • Approve writes audit_log backend-side.
//   • Reject reason is 7-enum dropdown + free-text on `other`.
//   • SLA badge: pending >2h yellow, >4h red.
//
// Implementation notes:
//   • Reads use TanStack Query `useQuery` keyed via `financeKeys.topupReviews`.
//   • Mutations (approve / reject) use `useMutation` + `qc.invalidateQueries`
//     on `financeKeys.topupReviews.all` so every list slice refetches after
//     a state change.
//   • Toast surfaces the canonical INTERFACE-STANDARDS message via
//     `getUserMessage(err)` — no `AxiosError.message` fallback ever.
//   • Audit log is backend-side; the FE only surfaces the success toast
//     referencing `audit_log_id` from the response.
//
// vg-binding: PLAN:task-06
// vg-binding: P8.D-84
// vg-binding: P8.D-214
// vg-binding: P8.D-447
// vg-binding: P8.D-448
// vg-binding: P8.D-449
// vg-binding: P8.D-450
// vg-binding: INTERFACE-STANDARDS:error-shape
// vg-binding: INTERFACE-STANDARDS:message-priority

// ---------------------------------------------------------------------------
// Local helpers
// ---------------------------------------------------------------------------

function reviewSlaPillClass(b: TopupReviewSlaBadge): string {
  switch (b) {
    case 'green':
      return 'bg-emerald-100 dark:bg-emerald-950/40 text-emerald-800 dark:text-emerald-300 border-emerald-200 dark:border-emerald-800';
    case 'yellow':
      return 'bg-amber-100 dark:bg-amber-950/40 text-amber-800 dark:text-amber-300 border-amber-200 dark:border-amber-800';
    case 'red':
      return 'bg-rose-100 dark:bg-rose-950/40 text-rose-800 dark:text-rose-300 border-rose-200 dark:border-rose-800';
  }
}

function reviewVerificationPillClass(v: 'auto_verified' | 'manual_review'): string {
  return v === 'auto_verified'
    ? 'bg-emerald-50 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-300 border-emerald-200 dark:border-emerald-800'
    : 'bg-amber-50 dark:bg-amber-950/40 text-amber-800 dark:text-amber-300 border-amber-200 dark:border-amber-800';
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * `<TopupReviewQueuePage>` — Phase 8.2 Finance Topup Reviews tab.
 *
 * Dual-branch list:
 *   • auto_verified rows expose an inline 1-click Approve button.
 *   • manual_review rows open `<TopupProofReviewModal>` for the 4-section
 *     review (IMAP/memo/screenshot/wallet) + approve/reject footer.
 *
 * URL-state synced filters (status + verification_status + sla + sort).
 * SLA badge colour (2h yellow / 4h red).
 */
export function TopupReviewQueuePage(): JSX.Element {
  const { toast } = useToast();
  const { t } = useTranslation('finance');
  const qc = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();

  // ---- URL state ---------------------------------------------------------
  const statusParam = (searchParams.get('status') as
    | 'pending'
    | 'approved'
    | 'rejected'
    | 'all'
    | null) ?? 'pending';
  const verificationParam = (searchParams.get('verification') as
    | 'auto_verified'
    | 'manual_review'
    | 'all'
    | null) ?? 'all';
  const slaParam = (searchParams.get('sla') as
    | 'fresh'
    | 'yellow_2h'
    | 'red_4h'
    | 'all'
    | null) ?? 'all';
  const merchantParam = searchParams.get('merchant') ?? '';
  // Phase 8.2 audit 2026-05-23 — URL-synced page + pageSize per /stores
  // standard. Live values clamped to BE allowlist (1..100); default 25 matches
  // the PageListFooter pageSizeChoices [10, 25, 50, 100].
  const reviewPage = Math.max(
    1,
    Number.parseInt(searchParams.get('page') ?? '1', 10) || 1,
  );
  const rawReviewPageSize = Number.parseInt(
    searchParams.get('pageSize') ?? '25',
    10,
  );
  const reviewPageSize = Number.isFinite(rawReviewPageSize) && rawReviewPageSize > 0
    ? Math.min(100, Math.max(10, rawReviewPageSize))
    : 25;

  const filters: TopupReviewListFilters = useMemo(
    () => ({
      status: statusParam,
      verification_status: verificationParam,
      age_bucket: slaParam,
      ...(merchantParam ? { merchant_id: merchantParam } : {}),
      sort: 'created_at',
      limit: reviewPageSize,
      page: reviewPage,
    }),
    [statusParam, verificationParam, slaParam, merchantParam, reviewPage, reviewPageSize],
  );

  // ---- Data fetch --------------------------------------------------------
  const listQuery = useQuery({
    queryKey: financeKeys.topupReviews.list(filters),
    queryFn: () => listTopupReviews(filters),
    staleTime: 15_000,
  });

  // ---- Modal state -------------------------------------------------------
  const [modalRow, setModalRow] = useState<TopupReviewRow | null>(null);

  // ---- MerchantPicker trigger label --------------------------------------
  const [merchantLabel, setMerchantLabel] = useState<string | null>(null);

  // ---- Mutations ---------------------------------------------------------
  const approveMutation = useMutation<
    ApproveTopupResponse,
    unknown,
    { id: string; notes?: string }
  >({
    // Phase 8.2 debug 2026-05-23 — Tier-1 gateway rows (paypal/stripe/
    // payoneer/lianlian) that are stuck pending throw TIER_1_AUTO_APPROVED
    // unless `force_override + override_reason` is sent. The inline approve
    // CTA always passes both so a single click works for both Tier-1 stuck
    // rows AND regular Tier-2 manual rows (the override is ignored on
    // Tier-2). Reason is fixed to a non-empty audit string.
    mutationFn: ({ id, notes }) =>
      approveTopup(id, {
        ...(notes ? { notes } : {}),
        force_override: true,
        override_reason:
          'Manual approve via /admin/billing/topup-reviews inline CTA (audit log)',
      }),
    onSuccess: (res) => {
      // dbg-20260609-multi: optional-chain the ref — BE returns
      // `ledger_entry_id` (not `audit_log_id`). A hard `.slice` on an
      // undefined field threw inside onSuccess on a 200 response, which also
      // aborted the invalidateQueries below (the "no auto-reload" symptom).
      const ref = res.ledger_entry_id?.slice(-8);
      toast({
        title: t('topup_review.queue.approveSuccess'),
        description: ref
          ? t('topup_review.reviews.approveSuccessDescRef', { ref })
          : t('topup_review.reviews.approveSuccessDesc'),
        variant: 'success',
      });
      setModalRow(null);
      void qc.invalidateQueries({ queryKey: financeKeys.topupReviews.all });
    },
    onError: (err) => {
      toast({
        title: t('topup_review.queue.approveFailed'),
        description: getUserMessage(err),
        variant: 'error',
      });
    },
  });

  const rejectMutation = useMutation<
    RejectTopupResponse,
    unknown,
    { id: string; reason: ModalTopupRejectReason; reason_text?: string }
  >({
    mutationFn: ({ id, reason, reason_text }) =>
      rejectTopup(id, {
        reason,
        notify_merchant: true,
        ...(reason_text ? { reason_text } : {}),
      }),
    onSuccess: (res) => {
      // dbg-20260609-multi: BE reject returns `{ id, status, reason,
      // rejected_at }` — no `notified`/`audit_log_id`. Show the reason
      // instead of slicing an undefined audit id (same crash class as approve).
      toast({
        title: t('topup_review.queue.rejectSuccess'),
        description: res.reason
          ? t('topup_review.reviews.rejectSuccessDescReason', { reason: res.reason })
          : t('topup_review.reviews.rejectSuccessDesc'),
        variant: 'success',
      });
      setModalRow(null);
      void qc.invalidateQueries({ queryKey: financeKeys.topupReviews.all });
    },
    onError: (err) => {
      toast({
        title: t('topup_review.queue.rejectFailed'),
        description: getUserMessage(err),
        variant: 'error',
      });
    },
  });

  // ---- URL helpers -------------------------------------------------------
  function updateParam(key: string, value: string): void {
    const next = new URLSearchParams(searchParams);
    // Delete the param only when it equals that key's DEFAULT so the URL stays
    // clean; otherwise write it explicitly. `status` defaults to 'pending', so
    // selecting 'all' MUST be written — deleting it reverted to 'pending' (the
    // "select all does nothing" bug, debug 2026-07-14). Other filters default
    // to 'all'.
    const isDefault =
      key === 'status' ? value === 'pending' : !value || value === 'all';
    if (isDefault) {
      next.delete(key);
    } else {
      next.set(key, value);
    }
    // Filter change resets to page 1 so user never lands on stale tail.
    if (key !== 'page' && key !== 'pageSize') next.delete('page');
    setSearchParams(next, { replace: true });
  }

  function setReviewPage(nextPage: number): void {
    const next = new URLSearchParams(searchParams);
    if (nextPage <= 1) next.delete('page');
    else next.set('page', String(nextPage));
    setSearchParams(next, { replace: true });
  }

  function handleReviewPageSizeChange(size: number): void {
    const next = new URLSearchParams(searchParams);
    next.set('pageSize', String(size));
    next.delete('page'); // reset to page 1
    setSearchParams(next, { replace: true });
  }

  const rows = listQuery.data?.rows ?? [];
  const overdueCount = listQuery.data?.meta.overdue_count ?? 0;
  const reviewTotal = listQuery.data?.meta.total ?? 0;
  const reviewTotalPages = Math.max(1, Math.ceil(reviewTotal / reviewPageSize));

  return (
    <div className="flex flex-col gap-6 p-6">
      {/* Header */}
      <PageHeader
        title={t('topup_review.reviews.title')}
        subtitle={t('topup_review.reviews.subtitle')}
        actions={
          overdueCount > 0 ? (
            <div className="flex items-center gap-2 rounded-md border border-rose-200 dark:border-rose-800 bg-rose-50 dark:bg-rose-950/40 px-3 py-2 text-sm font-medium text-rose-800 dark:text-rose-300">
              <span aria-hidden="true">⚠</span>
              <span>{t('topup_review.reviews.overdue', { count: overdueCount })}</span>
            </div>
          ) : null
        }
      />

      {/* Filters */}
      <Card>
        <CardContent className="flex flex-wrap items-end gap-3 py-4">
          <label className="flex flex-col text-xs font-medium text-pw-text-muted">
            {t('topup_review.reviews.filter.status')}
            <Select
              value={statusParam}
              onValueChange={(value) => updateParam('status', value)}
            >
              <SelectTrigger className="mt-1 w-40" data-testid="topup-status-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="pending">{t('topup_review.status.pending')}</SelectItem>
                <SelectItem value="approved">{t('topup_review.status.approved')}</SelectItem>
                <SelectItem value="rejected">{t('topup_review.status.rejected')}</SelectItem>
                <SelectItem value="all">{t('topup_review.reviews.filter.all')}</SelectItem>
              </SelectContent>
            </Select>
          </label>
          <label className="flex flex-col text-xs font-medium text-pw-text-muted">
            {t('topup_review.reviews.filter.verification')}
            <Select
              value={verificationParam}
              onValueChange={(value) => updateParam('verification', value)}
            >
              <SelectTrigger className="mt-1 w-44" data-testid="topup-verification-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('topup_review.reviews.filter.all')}</SelectItem>
                <SelectItem value="auto_verified">{t('topup_review.reviews.verification.auto')}</SelectItem>
                <SelectItem value="manual_review">{t('topup_review.reviews.verification.manual')}</SelectItem>
              </SelectContent>
            </Select>
          </label>
          <label className="flex flex-col text-xs font-medium text-pw-text-muted">
            {t('topup_review.reviews.filter.sla')}
            <Select
              value={slaParam}
              onValueChange={(value) => updateParam('sla', value)}
            >
              <SelectTrigger className="mt-1 w-40" data-testid="topup-sla-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('topup_review.reviews.filter.all')}</SelectItem>
                <SelectItem value="fresh">{t('topup_review.reviews.filter.slaFresh')}</SelectItem>
                <SelectItem value="yellow_2h">{t('topup_review.reviews.filter.slaYellow')}</SelectItem>
                <SelectItem value="red_4h">{t('topup_review.reviews.filter.slaRed')}</SelectItem>
              </SelectContent>
            </Select>
          </label>
          <label className="flex flex-col text-xs font-medium text-pw-text-muted">
            {t('topup_review.reviews.filter.merchant')}
            <div className="mt-1 w-64">
              <MerchantPicker
                value={merchantParam || null}
                valueLabel={merchantLabel}
                onChange={(id, label) => {
                  setMerchantLabel(label);
                  updateParam('merchant', id ?? '');
                }}
                placeholder={t('topup_review.queue.filter.allMerchants')}
                testId="topup-reviews-merchant-filter"
              />
            </div>
          </label>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {listQuery.isLoading ? (
            <div className="flex items-center justify-center p-12 text-sm text-pw-text-muted">
              {t('topup_review.reviews.loading')}
            </div>
          ) : listQuery.isError ? (
            <div className="flex flex-col items-center gap-2 p-12 text-sm text-red-700 dark:text-red-300">
              <span>{t('topup_review.reviews.errorTitle')}</span>
              <span className="text-pw-text-muted">
                {getUserMessage(listQuery.error)}
              </span>
              <Button
                size="sm"
                variant="secondary"
                onClick={() => void listQuery.refetch()}
              >
                {t('topup_review.reviews.retry')}
              </Button>
            </div>
          ) : rows.length === 0 ? (
            <div className="flex flex-col items-center gap-2 p-12 text-center text-sm text-pw-text-muted">
              <span className="text-base font-medium text-pw-text">
                {t('topup_review.reviews.emptyTitle')}
              </span>
              <span>{t('topup_review.reviews.emptyBody')}</span>
            </div>
          ) : (
            <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-pw-surface">
                <tr className="text-left text-xs font-medium uppercase tracking-wide text-pw-text-muted">
                  <th className="px-4 py-3">{t('topup_review.reviews.column.merchant')}</th>
                  <th className="px-4 py-3 text-right">{t('topup_review.reviews.column.amount')}</th>
                  <th className="px-4 py-3">{t('topup_review.reviews.column.verification')}</th>
                  <th className="px-4 py-3">{t('topup_review.reviews.column.sla')}</th>
                  <th className="px-4 py-3">{t('topup_review.reviews.column.created')}</th>
                  <th className="px-4 py-3">{t('topup_review.reviews.column.status')}</th>
                  <th className="px-4 py-3">{t('topup_review.reviews.column.rejectReason')}</th>
                  <th className="px-4 py-3">{t('topup_review.reviews.column.reviewer')}</th>
                  <th className="px-4 py-3 text-right">{t('topup_review.reviews.column.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => {
                  const isPending = row.status === 'pending';
                  const isAuto = row.verification_status === 'auto_verified';
                  const submitting =
                    (approveMutation.isPending &&
                      approveMutation.variables?.id === row.id) ||
                    (rejectMutation.isPending &&
                      rejectMutation.variables?.id === row.id);
                  return (
                    <tr
                      key={row.id}
                      className="border-t border-pw-border hover:bg-pw-surface"
                    >
                      <td className="px-4 py-3 align-top">
                        <div
                          className="font-medium text-pw-text"
                          title={row.merchant_id}
                        >
                          {/* Phase 8.2 debug Cluster C view #6 — when the BE
                              owner lookup couldn't resolve a display name,
                              `merchant_name` falls back to the full 24-char
                              hex. Detect that and render the short-id pattern
                              instead so the cell never shows a bare hex. */}
                          {row.merchant_name &&
                          row.merchant_name !== row.merchant_id
                            ? row.merchant_name
                            : t('topup_review.reviews.merchantFallback', { id: row.merchant_id.slice(-6) })}
                        </div>
                        {/* dbg-20260714: show owner email instead of the raw
                            merchant _id. Full merchant_id stays on the name
                            row's title tooltip for support lookups. */}
                        <div
                          className="text-xs text-pw-text-muted"
                          title={row.merchant_email ?? undefined}
                        >
                          {row.merchant_email ?? ''}
                        </div>
                      </td>
                      <td className="px-4 py-3 align-top text-right">
                        <Money usdCents={row.amount_cents} align="right" />
                      </td>
                      <td className="px-4 py-3 align-top">
                        <span
                          className={cn(
                            'inline-flex items-center rounded-md border px-2 py-0.5 text-xs font-medium',
                            reviewVerificationPillClass(row.verification_status),
                          )}
                        >
                          {row.verification_status === 'auto_verified'
                            ? t('topup_review.reviews.verification.auto')
                            : t('topup_review.reviews.verification.manual')}
                        </span>
                      </td>
                      <td className="px-4 py-3 align-top">
                        <span
                          className={cn(
                            'inline-flex items-center rounded-md border px-2 py-0.5 text-xs font-medium',
                            reviewSlaPillClass(row.sla_badge),
                          )}
                          title={t('topup_review.reviews.ageMinutes', { count: row.age_minutes })}
                        >
                          {t(`topup_review.reviews.slaBadge.${row.sla_badge}`)}
                        </span>
                      </td>
                      <td className="px-4 py-3 align-top text-xs text-pw-text-muted">
                        {new Date(row.created_at).toLocaleString()}
                      </td>
                      <td className="px-4 py-3 align-top">
                        {/* dbg-20260609-multi: surface the row status — there
                            is a Status filter but the list had no Status column,
                            so users couldn't see what state each row was in. */}
                        <BillingStatusBadge status={row.status} />
                      </td>
                      <td className="px-4 py-3 align-top text-sm text-pw-text-muted max-w-[220px]">
                        {row.status === 'rejected'
                          ? formatRejectReason(t, row.reject_reason)
                          : '—'}
                      </td>
                      <td className="px-4 py-3 align-top text-sm">
                        {row.reviewed_by ? (
                          <div className="flex flex-col">
                            <span className="text-pw-text">{row.reviewed_by}</span>
                            {row.reviewed_by_email &&
                            row.reviewed_by_email !== row.reviewed_by ? (
                              <span className="text-xs text-pw-text-muted">
                                {row.reviewed_by_email}
                              </span>
                            ) : null}
                          </div>
                        ) : (
                          <span className="text-pw-text-muted">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3 align-top text-right">
                        {!isPending ? (
                          <span className="text-xs capitalize text-pw-text-muted">
                            {t(`topup_review.status.${row.status}`)}
                          </span>
                        ) : isAuto ? (
                          // Auto-verified: quick Approve for the happy path, but
                          // ALSO a Review action so a wrong auto-verify can be
                          // opened and rejected (avoids one-click accept-by-
                          // mistake with no reject path — user request).
                          <div className="flex items-center justify-end gap-2">
                            <Button
                              size="sm"
                              variant="default"
                              data-testid="topup-approve-inline-btn"
                              disabled={submitting}
                              onClick={() =>
                                approveMutation.mutate({ id: row.id })
                              }
                            >
                              {submitting
                                ? t('topup_review.reviews.approving')
                                : t('topup_review.queue.approve')}
                            </Button>
                            <Button
                              size="sm"
                              variant="secondary"
                              data-testid="topup-review-open-btn"
                              disabled={submitting}
                              onClick={() => setModalRow(row)}
                            >
                              {t('topup_review.queue.review')}
                            </Button>
                          </div>
                        ) : (
                          <Button
                            size="sm"
                            variant="secondary"
                            data-testid="topup-review-open-btn"
                            onClick={() => setModalRow(row)}
                          >
                            {t('topup_review.queue.review')}
                          </Button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            </div>
          )}
          {/* Pagination — canonical /stores footer (Rows-per-page + Total +
              numbered) per audit 2026-05-23 Cluster A. */}
          {reviewTotal > 0 ? (
            <div className="border-t border-pw-border px-6 py-3">
              <PageListFooter
                page={reviewPage}
                pageSize={reviewPageSize}
                total={reviewTotal}
                totalPages={reviewTotalPages}
                loading={listQuery.isFetching}
                onPageChange={setReviewPage}
                onPageSizeChange={handleReviewPageSizeChange}
                testId="topup-reviews-footer"
              />
            </div>
          ) : null}
        </CardContent>
      </Card>

      {/* Modal — manual-review branch */}
      {modalRow ? (
        <TopupProofReviewModal
          open={!!modalRow}
          onOpenChange={(o) => {
            if (!o) setModalRow(null);
          }}
          row={modalRow}
          submitting={approveMutation.isPending || rejectMutation.isPending}
          onApprove={async ({ notes }) => {
            await approveMutation.mutateAsync({ id: modalRow.id, notes });
          }}
          onReject={async ({ reason, reason_text }) => {
            await rejectMutation.mutateAsync({
              id: modalRow.id,
              reason,
              ...(reason_text ? { reason_text } : {}),
            });
          }}
        />
      ) : null}
    </div>
  );
}

// (No `export default` — the original `TopupQueuePage` keeps the default
// export; `TopupReviewQueuePage` is a named export consumed by the new
// `/billing/topup-reviews` route registered in App.tsx Wave 2 routes.)
